/**
 * Resumable (TUS) uploads to Supabase Storage.
 *
 * Shared by services/classes.ts and services/helpVideos.ts. Both move files
 * measured in hundreds of megabytes, and the ordinary `storage.upload()` is a
 * single request holding the whole body: one dropped connection at 90% and the
 * operator starts again from zero. TUS uploads in 6 MB chunks and resumes from
 * the last acknowledged one.
 *
 * Extracted rather than copied because the two non-obvious details here — the
 * per-request auth refresh and the abort that has to reject its own promise —
 * are exactly the kind of thing that gets fixed in one copy and not the other.
 */
import type * as TusTypes from 'tus-js-client';
import { supabase, SUPABASE_URL } from './supabase';

/** tus is ~50 KB and only ever used by a platform admin uploading a recording.
 *  A static import puts it in the eagerly-preloaded vendor chunk, so every
 *  visitor — including anonymous landing-page traffic — would download it.
 *  vite.config.ts has a matching manualChunks entry keeping it out of `vendor`. */
const loadTus = () => import('tus-js-client');

/** Supabase's TUS endpoint requires exactly 6 MB chunks (except the last). */
const CHUNK_SIZE = 6 * 1024 * 1024;

/** A deliberate cancellation, not a failure. Its own type because matching on
 *  an error message's text is a guess, and telling someone their upload broke
 *  right after they cancelled it reads as a bug they caused. */
export class UploadAborted extends Error {
    constructor() {
        super('upload:aborted');
        this.name = 'UploadAborted';
    }
}

/** The server refused the size. Distinct from any client-side check: Supabase
 *  enforces a PROJECT-WIDE ceiling (Settings → Storage → "Upload file size
 *  limit") that sits above every bucket and wins whenever it is lower. It
 *  defaults to 50 MB, so a bucket configured for gigabytes still rejects a
 *  146 MB file until that project setting is raised — and the raw tus text
 *  ("unexpected response while creating upload… 413") names neither which
 *  limit was hit nor where to change it. */
export class UploadTooLargeForServer extends Error {
    constructor() {
        super('upload:server-limit');
        this.name = 'UploadTooLargeForServer';
    }
}

export class UploadNotSignedIn extends Error {
    constructor() {
        super('upload:not-signed-in');
        this.name = 'UploadNotSignedIn';
    }
}

export interface UploadHandle {
    /** Stop the upload. The partial object is discarded by Storage. */
    abort: () => void;
    /** Resolves with the stored object path once the file is fully uploaded. */
    done: Promise<string>;
}

/** Map a tus failure onto something a UI can say out loud. */
export function classifyUploadError(err: unknown): unknown {
    const detailed = err as { originalResponse?: { getStatus?: () => number } };
    const status = detailed?.originalResponse?.getStatus?.();
    if (status === 413) return new UploadTooLargeForServer();

    // Not every tus error path attaches the response, so fall back to the text
    // rather than losing the distinction.
    const msg = (err as { message?: string })?.message ?? '';
    if (/\b413\b|maximum size exceeded/i.test(msg)) return new UploadTooLargeForServer();

    return err;
}

/**
 * Upload `file` to `bucket` at `path`, resumably.
 *
 * Returns a handle rather than a bare promise because an upload measured in
 * tens of minutes needs a visible progress bar and a way out — a modal the
 * operator cannot cancel is not acceptable at this file size.
 *
 * Nothing is written to the database here. The caller attaches the returned
 * path once the upload resolves, so a cancelled or failed upload leaves the
 * row pointing at whatever it pointed at before.
 */
export function resumableUpload(
    bucket: string,
    path: string,
    file: File,
    onProgress: (sentBytes: number, totalBytes: number) => void,
    fallbackContentType = 'application/octet-stream',
): UploadHandle {
    let upload: TusTypes.Upload | null = null;
    let aborted = false;
    let settle: { reject: (e: unknown) => void } | null = null;

    const done = new Promise<string>((resolve, reject) => {
        settle = { reject };

        Promise.all([loadTus(), supabase.auth.getSession()]).then(([tus, { data }]) => {
            if (!data.session?.access_token) return reject(new UploadNotSignedIn());
            if (aborted) return reject(new UploadAborted());

            upload = new tus.Upload(file, {
                endpoint: `${SUPABASE_URL}/storage/v1/upload/resumable`,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                headers: {
                    // Paths are unique per upload, so this only matters for a
                    // retry landing on the same object.
                    'x-upsert': 'true',
                },
                // THE AUTH HEADER IS SET PER REQUEST, NOT ONCE.
                //
                // Supabase access tokens last one hour. These uploads can last
                // longer. Baking the token into `headers` above — the obvious
                // way, and what every Supabase TUS example shows — means every
                // chunk after the 60-minute mark 401s, and tus treats 401 as
                // fatal. The upload would die around halfway with no resume,
                // which is precisely what resumable uploads exist to prevent.
                //
                // getSession() refreshes a token close to expiry, so asking per
                // request keeps a long upload authorised throughout.
                onBeforeRequest: async req => {
                    const { data: fresh } = await supabase.auth.getSession();
                    const token = fresh.session?.access_token;
                    if (token) req.setHeader('authorization', `Bearer ${token}`);
                },
                uploadDataDuringCreation: true,
                // Paths carry a random suffix, so a new upload of the same file
                // is a different object; keeping the fingerprint after success
                // would only accumulate dead localStorage keys. Mid-upload
                // resumption is handled by retryDelays above.
                removeFingerprintOnSuccess: true,
                metadata: {
                    bucketName: bucket,
                    objectName: path,
                    contentType: file.type || fallbackContentType,
                    cacheControl: '3600',
                },
                chunkSize: CHUNK_SIZE,
                onError: err => reject(classifyUploadError(err)),
                onProgress: (sent, total) => onProgress(sent, total),
                onSuccess: () => resolve(path),
            });

            upload.start();
        }).catch(reject);
    });

    return {
        abort: () => {
            if (aborted) return;
            aborted = true;
            void upload?.abort(true);
            // tus guarantees NO handler fires after abort(), so `done` would
            // hang forever and the caller's finally block — the one that clears
            // the progress bar — would never run. Reject it ourselves.
            settle?.reject(new UploadAborted());
        },
        done,
    };
}

/** Object path for a fresh upload. Every upload gets a NEW path: a file never
 *  overwrites the old one in place, so a viewer midway through the previous
 *  video is not yanked onto the new one and the CDN has no stale copy.
 *
 *  The random suffix is load-bearing, not decoration. Uploads go up with
 *  `upsert: false` on the non-resumable paths, so two files landing on the same
 *  path is an outright "resource already exists" failure, and a bare
 *  Date.now() collides whenever two uploads start inside the same millisecond. */
export function uniqueObjectPath(prefix: string, kind: string, file: File, fallbackExt: string): string {
    const raw = file.name.split('.').pop();
    const ext = raw && /^[a-z0-9]{1,5}$/i.test(raw) ? raw.toLowerCase() : fallbackExt;
    const stamp = Date.now().toString(36);
    const rand = Math.random().toString(36).slice(2, 8);
    return `${prefix}/${kind}-${stamp}-${rand}.${ext}`;
}
